import { useEffect, useState } from 'react'
import { BasicByCodeCache } from '../basicByCodeCache'

export type BasicLookup<T> =
  | { status: 'loading' }
  | { status: 'resolved'; basic: T }
  | { status: 'unavailable' }

const toLookup = <T>(basic: T | null | undefined): BasicLookup<T> => {
  if (basic === undefined) {
    return { status: 'loading' }
  }
  return basic ? { status: 'resolved', basic } : { status: 'unavailable' }
}

/**
 * コードから実体を取得する（キャッシュ・重複排除は basicByCodeCache 側）。
 *
 * 取得できない（404 = モデルの幻覚 / 403 = 参照権限なし）場合も unavailable を返すだけで、
 * ここでは例外を投げない・通知も出さない。呼び出し側（各チップ）がチップをやめて
 * 本文のプレーンテキストに切り替える判断材料として使う。
 */
export const useBasicByCode = <T>(
  cache: BasicByCodeCache<T>,
  code: string
): BasicLookup<T> => {
  const [lookup, setLookup] = useState<BasicLookup<T>>(() =>
    toLookup(cache.peek(code))
  )

  useEffect(() => {
    let cancelled = false
    const cached = cache.peek(code)
    if (cached !== undefined) {
      setLookup(toLookup(cached))
      return
    }
    setLookup({ status: 'loading' })
    cache.load(code).then(basic => {
      if (!cancelled) {
        setLookup(toLookup(basic))
      }
    })
    return () => {
      cancelled = true
    }
  }, [cache, code])

  return lookup
}
